import { ArrowRight } from 'lucide-react'

import applicationImage from '@/assets/application.png'
import quotesImage from '@/assets/quotes.png'
import { Anchor } from '@/components/ui/Anchor'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Every point of the buying journey the account banner could speak to. */
export enum AccountStage {
  APPLICATION_INCOMPLETE = 'APPLICATION_INCOMPLETE',
  QUOTES_READY = 'QUOTES_READY',
  CHECKOUT_STARTED = 'CHECKOUT_STARTED',
  WITH_BROKER = 'WITH_BROKER',
  CHECKING_OUT = 'CHECKING_OUT',
  ALL_ACTIVE = 'ALL_ACTIVE',
}

const TOTAL_DOTS = 5

// Faded out well before the right edge so it never sits under the illustrations.
const GRID_FADE = 'linear-gradient(to right, #000 0%, #000 30%, transparent 60%)'

// 90° minus the 34° lean measured off the illustrations' parallelogram edges, which
// slant their uprights but keep their top and bottom edges level.
const GRID_TILT = 56

// currentColor picks up the banner tone's ink, so the grid tints with the variant.
const GRID_TEXTURE: React.CSSProperties = {
  backgroundImage: `repeating-linear-gradient(${GRID_TILT}deg, currentColor 0 1px, transparent 1px 16px), repeating-linear-gradient(180deg, currentColor 0 1px, transparent 1px 16px)`,
  maskImage: GRID_FADE,
  WebkitMaskImage: GRID_FADE,
}

interface StageCopy {
  title: React.ReactNode
  body: React.ReactNode
  cta: string
  href: string
  /** Filled dots — two pairs of stages share a step, so this is not the stage's index. */
  dots: number
  /** Nothing is being asked of the customer, so the CTA steps back to outline. */
  passive: boolean
  /** Greys the banner out — the journey is paused on someone other than the customer. */
  muted?: boolean
  /** Sits under the CTA in the right-hand column. */
  image?: string
}

// WITH_BROKER copy tracks the shipped OnboardingBanner — change it there first. Every
// other stage has deliberately diverged from it.
const COPY: Record<AccountStage, StageCopy> = {
  [AccountStage.APPLICATION_INCOMPLETE]: {
    title: 'Continue your insurance application',
    body: (
      <>
        You’re minutes away from getting quotes{' '}
        {/* Desktop only — at phone width the copy already wraps here on its own. */}
        <br className="max-sm:hidden" />
        from top insurance providers.
      </>
    ),
    cta: 'Finish application',
    href: '/application',
    dots: 1,
    passive: false,
    image: applicationImage,
  },
  [AccountStage.QUOTES_READY]: {
    title: 'Review your quotes',
    body: (
      <>
        Compare offers on your quotes page, <br className="max-sm:hidden" />
        and choose coverages that are right for your fleet.
      </>
    ),
    cta: 'Go to quotes',
    href: '/application',
    dots: 2,
    passive: false,
    image: quotesImage,
  },
  [AccountStage.CHECKOUT_STARTED]: {
    title: 'Coverages are waiting in your cart',
    body: 'Request checkout, and your broker will finalize pricing, collect payment, and activate your coverage.',
    cta: 'Go to quotes',
    href: '/application',
    dots: 2,
    passive: false,
    image: quotesImage,
  },
  [AccountStage.WITH_BROKER]: {
    title: (
      <>
        Your broker is finalizing your <br className="max-sm:hidden" />
        requested coverage
      </>
    ),
    body: 'They’ll be in touch with next steps once underwriting is complete.',
    cta: 'View your quotes',
    href: '/application',
    dots: 3,
    passive: true,
    muted: true,
  },
  [AccountStage.CHECKING_OUT]: {
    title: 'Complete your checkout',
    body: 'Your final pricing is ready. Review it and share your payment details to activate your coverage.',
    cta: 'Complete checkout',
    href: '/application',
    dots: 4,
    passive: false,
  },
  [AccountStage.ALL_ACTIVE]: {
    title: 'All your coverage is active',
    body: 'Every policy you bought is in force. Your certificates are ready whenever you need them.',
    cta: 'View your policies',
    href: '/insure/insurance',
    dots: 5,
    passive: true,
  },
}

/** How far along the purchase is — bg-current so the dots take the banner's own ink. */
function StepDots({ filled, className }: { filled: number; className?: string }) {
  return (
    <span
      role="img"
      aria-label={`Step ${filled} of ${TOTAL_DOTS}`}
      className={cn('flex shrink-0 items-center gap-1.5', className)}
    >
      {Array.from({ length: TOTAL_DOTS }, (_, i) => (
        <span
          key={i}
          className={cn(
            'size-1.5 rounded-full bg-current',
            i >= filled && 'opacity-25',
            i === filled - 1 && 'animate-pulse motion-reduce:animate-none'
          )}
        />
      ))}
    </span>
  )
}

/** The account banner with a step indicator, at any point of the journey. */
export function AccountBanner({ stage }: { stage: AccountStage }) {
  const copy = COPY[stage]

  return (
    <Banner
      variant="info"
      className={cn(
        'relative grid min-h-[200px] items-center overflow-hidden p-8',
        copy.muted && 'border-ink-200 bg-ink-100 text-ink-700'
      )}
      icon={null}
      action={
        // relative lifts the CTA over the artwork — both positioned, so DOM order wins.
        <Button
          href={copy.href}
          variant={copy.passive ? 'outline' : 'default'}
          className="relative max-sm:w-full"
        >
          {copy.cta}
          {!copy.passive && <ArrowRight />}
        </Button>
      }
    >
      {copy.image && (
        <img src={copy.image} alt="" className="absolute top-0 right-0 h-full w-auto max-sm:hidden" />
      )}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-5" style={GRID_TEXTURE} />
      <div className="relative max-w-lg min-w-0">
        <strong className="block text-2xl font-bold tracking-[-0.01em]">{copy.title}</strong>
        <p className="text-md mt-2 leading-relaxed">{copy.body}</p>
        <StepDots filled={copy.dots} className="mt-4" />
      </div>
      {/* Last child, so it covers the copy; the CTA renders after it and stays on top. */}
      <Anchor href={copy.href} aria-hidden tabIndex={-1} className="absolute inset-0" />
    </Banner>
  )
}
